// Design agent: Spec 013 — sysadmins want the numbers MasqIP already computes (inspector insights,
// VLSM plans) outside the browser tab: in a spreadsheet, or as ready-to-paste BIND zone text. This
// module is the single place that turns already-computed rows into those three text formats, kept
// framework-free (no React, no DOM) so it stays trivially unit-testable and reusable by any future
// export target.
// Developer agent: Every function here is pure (string in, string out) — no Blob, no clipboard, no
// anchor download. Those browser-only side effects belong in the ExportMenu component, which is the
// only place that needs `document`/`navigator` and is therefore harder to unit test under bun+happy-dom.
import { buildDottedBinary } from './binaryBreakdown';
import { intToIpv4 } from './ipMath';

// Design agent: A single spreadsheet cell must never be allowed to break the column structure — a
// comma, a double quote, or an embedded newline in a value (address labels can contain any of these
// in theory, e.g. a subnet name typed by the user) would otherwise shift every following column.
// Developer agent: Implements the standard RFC 4180 escaping rule: wrap the cell in double quotes
// whenever it contains a comma, a quote, or a newline, doubling any quote characters it already had.
function escapeCsvCell(cellValue) {
  const text = cellValue === null || cellValue === undefined ? '' : String(cellValue);
  const needsQuoting = text.includes(',') || text.includes('"') || text.includes('\n') || text.includes('\r');
  if (!needsQuoting) {
    return text;
  }
  return `"${text.replace(/"/g, '""')}"`;
}

// Design agent: Builds a full CSV document (header row + data rows) from plain arrays, so callers
// never have to think about comma/quote escaping themselves — they just hand over the raw values.
// Developer agent: Uses CRLF line endings (`\r\n`) because that is what RFC 4180 and every major
// spreadsheet application (Excel included) expects for a `.csv` file, even though the rest of the
// codebase uses `\n` for JS source.
export function toCsv(headers, rows) {
  const headerLine = headers.map(escapeCsvCell).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvCell).join(','));
  return [headerLine, ...dataLines].join('\r\n');
}

// Design agent: A cell pasted into Excel/LibreOffice via the clipboard only needs its own tabs and
// newlines neutralised (a real tab or newline inside a cell would otherwise create a phantom column
// or row) — spreadsheet paste does not use CSV's quote-escaping convention, so keeping this separate
// from toCsv avoids stray quote characters showing up in every pasted cell.
// Developer agent: Replaces embedded tabs/newlines with a single space; commas are left untouched
// since TSV does not treat them as a delimiter.
function escapeTsvCell(cellValue) {
  const text = cellValue === null || cellValue === undefined ? '' : String(cellValue);
  return text.replace(/[\t\r\n]+/g, ' ');
}

// Design agent: Produces the tab-separated text that, once on the clipboard, pastes straight into
// spreadsheet columns — the "Copy for spreadsheet" export mode from spec 013.
// Developer agent: Uses plain `\n` between rows; every mainstream spreadsheet application accepts
// either line ending on paste, and this keeps the function's output easy to assert on in tests.
export function toTsv(headers, rows) {
  const headerLine = headers.map(escapeTsvCell).join('\t');
  const dataLines = rows.map((row) => row.map(escapeTsvCell).join('\t'));
  return [headerLine, ...dataLines].join('\n');
}

// Design agent: RFC 2317 classless in-addr.arpa delegation only makes sense for a block that is
// entirely contained inside a single /24 (prefixes /25 through /32); anything /24 or wider gets the
// ordinary, single-owner reverse zone. This mirrors exactly how a real DNS operator would name the
// zone: the delegated zone name is a property of the *block* (its own network address and prefix),
// while an ordinary /24-or-wider zone name is a property of whichever /24 a given host happens to
// fall inside — which is why the two branches read different inputs (network vs. host address).
// Developer agent: Because every VLSM subnet this function is ever called with is entirely contained
// within one /24 when its prefix is longer than 24 (a /25 or smaller block cannot straddle a /24
// boundary), the first and last usable host of such a subnet always share the same delegated zone
// name — no enumeration of intermediate /24s is ever required.
function buildReverseZoneName(hostAddressInt, subnetPrefix, subnetNetworkInt) {
  if (subnetPrefix > 24) {
    const [a, b, c, d] = intToIpv4(subnetNetworkInt).split('.').map(Number);
    return `${d}/${subnetPrefix}.${c}.${b}.${a}.in-addr.arpa`;
  }
  const [a, b, c] = intToIpv4(hostAddressInt).split('.').map(Number);
  return `${c}.${b}.${a}.in-addr.arpa`;
}

// Design agent: Turns one host address into the fully-qualified PTR record owner name a resolver
// would actually query, following the real RFC 2317 convention of prefixing the delegated zone name
// with the host's own last octet (e.g. `1.0/25.1.0.10.in-addr.arpa.`) so the record is self-contained
// and correct even when several delegated zones are mixed together in one exported text blob.
// Developer agent: Delegates the zone-name half of the computation to buildReverseZoneName so the
// "/24 or shorter" vs. "RFC 2317" branching logic lives in exactly one place.
function buildPtrOwnerName(hostAddressInt, subnetPrefix, subnetNetworkInt) {
  const lastOctet = intToIpv4(hostAddressInt).split('.')[3];
  const zoneName = buildReverseZoneName(hostAddressInt, subnetPrefix, subnetNetworkInt);
  return `${lastOctet}.${zoneName}.`;
}

// Design agent: Spec 013 item 3 — generates a self-contained, human-readable BIND-style text block
// (forward A records for the first and last usable host of each subnet, plus their matching PTR
// records) that a sysadmin can paste straight into a zone file or use as a drafting starting point.
// A short header comment documents the assumptions so nobody mistakes this for a production-ready
// zone file without review.
// Developer agent: `subnets` is a plain array of `{ name, network, prefix, firstHost, lastHost }`
// where network/firstHost/lastHost are 32-bit integers (not dotted strings) — both call sites
// (IpInspector's per-entry export and VlsmCalculator's per-allocation export) already have these as
// integers from their own analysis/allocation objects, so no parsing happens in here.
// `options.hostnamePattern` supports the two placeholders `{name}` and `{index}` (1-based subnet
// position); the last usable host reuses the same base name with a trailing "b" (second address of
// the pair) since the spec's example only illustrates the first-host case.
export function toBindRecords(subnets, options = {}) {
  const hostnamePattern = options.hostnamePattern && options.hostnamePattern.trim() !== ''
    ? options.hostnamePattern
    : '{name}-{index}';
  const domain = options.domain && options.domain.trim() !== '' ? options.domain : 'example.local';

  const lines = [];
  lines.push('; Generated by MasqIP — BIND-style forward and reverse records.');
  lines.push(`; Hostname pattern: "${hostnamePattern}" (edit it in the Export menu before copying).`);
  lines.push('; Assumption: one A/PTR pair per subnet for its first usable host, one for its last.');
  lines.push('; Reverse zones use RFC 2317 classless delegation for prefixes longer than /24.');
  lines.push('');

  const buildBaseName = (subnet, index) =>
    hostnamePattern.replace('{name}', subnet.name && subnet.name.trim() !== '' ? subnet.name.trim() : `subnet${index}`).replace('{index}', String(index));

  lines.push('; --- Forward zone (A records) ---');
  subnets.forEach((subnet, position) => {
    const index = position + 1;
    const baseName = buildBaseName(subnet, index);
    lines.push(`; Subnet "${subnet.name || `subnet${index}`}" — ${intToIpv4(subnet.network)}/${subnet.prefix}`);
    lines.push(`${baseName}.${domain}. IN A ${intToIpv4(subnet.firstHost)}`);
    lines.push(`${baseName}b.${domain}. IN A ${intToIpv4(subnet.lastHost)}`);
  });

  lines.push('');
  lines.push('; --- Reverse zone (PTR records) ---');
  subnets.forEach((subnet, position) => {
    const index = position + 1;
    const baseName = buildBaseName(subnet, index);
    const firstZoneName = buildReverseZoneName(subnet.firstHost, subnet.prefix, subnet.network);
    const lastZoneName = buildReverseZoneName(subnet.lastHost, subnet.prefix, subnet.network);
    lines.push(`; Subnet "${subnet.name || `subnet${index}`}" — reverse zone ${firstZoneName}`);
    lines.push(`${buildPtrOwnerName(subnet.firstHost, subnet.prefix, subnet.network)} IN PTR ${baseName}.${domain}.`);
    if (lastZoneName !== firstZoneName) {
      lines.push(`; note: last host falls under a different reverse zone (${lastZoneName})`);
    }
    lines.push(`${buildPtrOwnerName(subnet.lastHost, subnet.prefix, subnet.network)} IN PTR ${baseName}b.${domain}.`);
  });

  return lines.join('\n');
}

// Design agent: Exposes the same binary-dotted formatting already used by the on-screen popover
// (BinaryBreakdown) so the inspector CSV's "binary (dotted)" column reads identically to what the
// user already saw on screen, instead of introducing a second, possibly inconsistent formatter.
// Developer agent: Thin re-export kept in this file (rather than importing binaryBreakdown directly
// in IpInspector's CSV-row builder) so every export-format detail stays behind this module's API.
export function toBinaryDotted(address) {
  return buildDottedBinary(address);
}

// Design agent: Every downloaded file needs a stable, sortable, human-readable date stamp in its
// name so two exports taken on different days never collide and always sort chronologically.
// Developer agent: Reads the local date (not UTC) since that is what the user visually expects the
// "today" in the filename to mean; zero-pads month/day to keep the YYYYMMDD width constant.
export function buildDateStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}
